import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

/**
 * 거래명세표 출력.
 *
 * 받은 양식(public/templates/transaction-statement.xlsx)을 그대로 열어 값만 채운다.
 * HTML 로 다시 그리지 않는 이유: 이 양식은 병합 셀이 260개라 재현하면 반드시 미묘하게 틀어진다.
 * 양식이 바뀌면 이 파일 대신 템플릿만 갈아끼우면 된다.
 *
 * SheetJS(xlsx) 가 아니라 ExcelJS 를 쓰는 이유: SheetJS 무료판은 읽고 다시 쓸 때
 * 테두리·병합·서식이 상당 부분 날아간다. 양식지는 그게 곧 결과물이라 쓸 수 없다.
 *
 * 양식 파일을 갈아끼울 때 주의할 점이 있다.
 * 처음 받은 양식은 한셀(HCell)로 만든 것이었고, 그대로는 ExcelJS 가 열지 못했다. 두 가지 때문이다.
 *
 *   1. 모든 태그에 x: 접두사가 붙어 있었다(<x:worksheet>). ExcelJS 는 접두사 없는 태그만 본다.
 *   2. 서식(xf)이 <mc:AlternateContent> 로 감싸여 있었다. "아는 프로그램은 Choice, 모르면
 *      Fallback 을 써라" 라는 뜻인데 ExcelJS 는 이 태그를 몰라 안에 든 서식을 통째로 흘린다.
 *      그 결과 216개 서식 중 2개만 인식돼 표 테두리가 전부 사라졌다.
 *
 * 지금 들어 있는 templates/transaction-statement.xlsx 는 두 가지를 정리해 둔 것이다.
 * 한셀에서 새로 뽑은 파일로 덮으면 같은 증상이 다시 난다. Excel 로 다시 저장하거나
 * 같은 방식으로 정리한 뒤 넣어야 한다.
 */

const TEMPLATE_URL = '/templates/transaction-statement.xlsx';

/**
 * 양식은 한 장에 2부가 들어간다.
 *   1~20행  (공급받는자 보관용)
 *   23~42행 (공급자 보관용)
 * 내용은 같으므로 같은 값을 두 번 쓴다. offset 은 두 번째 부의 행 차이.
 */
const COPY_OFFSETS = [0, 22];

/** 품목 줄 수. 8~18행(상단 기준) */
const ITEM_START_ROW = 8;
const ITEM_ROW_COUNT = 11;

/**
 * 라벨 옆 값이 들어갈 칸.
 * 병합 셀은 시작 칸에만 쓰면 된다.
 */
const CELL = {
  date: 'P1',          // 일자
  docNo: 'X1',         // No.
  supplierTel: 'S2',   // 공급자연락처

  supplierNm: 'B4',    // 공급자 상호
  supplierCeo: 'K4',   // 공급자 성명
  supplierAddr: 'B5',  // 공급자 주소
  supplierBizType: 'B6', // 업태
  supplierBizItem: 'J6', // 종목

  buyerNm: 'S4',       // 공급받는자 상호
  buyerCeo: 'AB4',     // 공급받는자 성명
  buyerAddr: 'S5',     // 공급받는자 주소
  buyerRemark: 'S6',   // 비고
  receiver: 'AE6',     // 인수자

  totalAmt: 'C19',     // 합계
  priorBalance: 'AC19', // 전잔금
  memo: 'C20',         // 메모
  totalBalance: 'AC20', // 총잔금
};

/** 품목 행의 열 시작점 */
const ITEM_COL = {
  monthDay: 'A',   // 월일
  name: 'C',       // 품 명 / 규 격
  unit: 'M',       // 단위
  qty: 'P',        // 수량
  unitPrice: 'S',  // 단가
  supplyAmt: 'W',  // 공급가액
  vatAmt: 'AA',    // 세액
  remark: 'AE',    // 비고/합계
};

const num = (value) => {
  const n = Number(String(value ?? '').replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
};

/** 품명과 규격을 한 칸에 넣는다. 규격이 없거나 품명과 같으면 품명만 쓴다. */
const nameWithSpec = (item) => {
  const name = item.productNm || '';
  const spec = item.productSpec || '';
  if (!spec || spec === name) return name;
  return `${name} / ${spec}`;
};

const setCell = (sheet, address, value) => {
  if (value === undefined || value === null || value === '') return;
  sheet.getCell(address).value = value;
};

/**
 * @param {object} data  서버가 내려준 거래명세표 데이터
 * @param {object} extra 화면에서 보완한 값(공급받는자 대표자/업태/종목, 인수자, 메모)
 */
/**
 * 수식을 계산된 값으로 바꾼다.
 *
 * 양식에는 수식이 75개 있다. 둘째 사본이 첫째를 참조하고(B26=B4), 합계는 SUM 이다.
 * 이 상태로 시트를 복제하면 "Shared Formula master must exist" 로 터진다 —
 * 복제본의 수식이 원본의 master 를 가리키기 때문이다.
 *
 * 값으로 바꿔도 결과는 같다. 우리 코드가 두 사본과 합계를 직접 채우기 때문이다.
 */
const flattenFormulas = (sheet) => {
  sheet.eachRow({ includeEmpty: true }, (row) => {
    row.eachCell({ includeEmpty: true }, (cell) => {
      if (cell.type === ExcelJS.ValueType.Formula) {
        const value = cell.result;
        cell.value = value === undefined || value === null ? null : value;
      }
    });
  });
};

/**
 * 양식 한 장을 통째로 복제한다.
 *
 * 품목이 11줄을 넘으면 다음 장으로 넘겨야 하는데, 시트를 복사하면 병합이 통째로 날아간다.
 * (260개 → 0개) 그래서 병합·열너비·행높이를 손으로 다시 입힌다.
 */
const clonePage = (workbook, source, name) => {
  const merges = (source.model.merges || []).slice();
  const page = workbook.addWorksheet(name);
  page.model = { ...source.model, name, merges: [] };

  merges.forEach((range) => {
    try {
      page.mergeCells(range);
    } catch (e) {
      // 이미 병합된 범위. 무시해도 결과가 같다.
    }
  });
  source.columns.forEach((col, i) => {
    if (col && col.width) page.getColumn(i + 1).width = col.width;
  });
  source.eachRow({ includeEmpty: true }, (row, n) => {
    if (row.height) page.getRow(n).height = row.height;
  });
  return page;
};

/** 품목을 양식 한 장에 들어가는 만큼씩 끊는다. */
const chunk = (list, size) => {
  const out = [];
  for (let i = 0; i < list.length; i += size) out.push(list.slice(i, i + size));
  return out.length > 0 ? out : [[]];
};

export const downloadShipmentStatement = async (data, extra = {}) => {
  const response = await fetch(TEMPLATE_URL);
  if (!response.ok) {
    throw new Error('거래명세표 양식을 불러오지 못했습니다.');
  }

  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(await response.arrayBuffer());
  const template = workbook.worksheets[0];

  const supplier = data.supplier || {};
  const buyer = data.buyer || {};
  const allItems = data.items || [];

  // 11줄이 넘으면 장을 나눈다. 한 파일 안에 시트로 이어 붙인다.
  const pages = chunk(allItems, ITEM_ROW_COUNT);

  if (pages.length > 1) {
    flattenFormulas(template);
  }

  // 장을 먼저 다 만들어 둔다. 첫 장을 채운 뒤에 복제하면 그 내용이 딸려간다 —
  // 마지막 장이 앞 장보다 품목이 적을 때, 남은 줄에 앞 장 품목이 그대로 보인다.
  const sheets = pages.map((_, pageIndex) =>
    pageIndex === 0 ? template : clonePage(workbook, template, `${pageIndex + 1}쪽`),
  );
  if (pages.length > 1) template.name = '1쪽';

  pages.forEach((items, pageIndex) => {
  const sheet = sheets[pageIndex];

  COPY_OFFSETS.forEach((offset) => {
    const at = (address) => {
      const col = address.replace(/\d/g, '');
      const row = Number(address.replace(/[A-Z]/g, '')) + offset;
      return `${col}${row}`;
    };

    setCell(sheet, at(CELL.date), data.shipmentDate || '');
    setCell(sheet, at(CELL.docNo), data.shipmentNumber || '');
    setCell(sheet, at(CELL.supplierTel), supplier.telNo || '');

    setCell(sheet, at(CELL.supplierNm), supplier.companyNm || '');
    setCell(sheet, at(CELL.supplierCeo), supplier.ceoNm || '');
    setCell(sheet, at(CELL.supplierAddr), supplier.addr || '');
    setCell(sheet, at(CELL.supplierBizType), supplier.bizType || '');
    setCell(sheet, at(CELL.supplierBizItem), supplier.bizItem || '');

    setCell(sheet, at(CELL.buyerNm), buyer.partnerNm || '');
    // 거래처에 대표자·업태 항목이 없어 화면에서 받은 값으로 채운다.
    setCell(sheet, at(CELL.buyerCeo), extra.buyerCeo || buyer.ceoNm || '');
    setCell(sheet, at(CELL.buyerAddr), buyer.addr || '');
    setCell(sheet, at(CELL.buyerRemark), extra.remark || '');
    setCell(sheet, at(CELL.receiver), extra.receiver || '');

    // 품목
    items.slice(0, ITEM_ROW_COUNT).forEach((item, index) => {
      const row = ITEM_START_ROW + index + offset;
      // 묶어 뽑으면 줄마다 나간 날이 다르다. 줄의 날짜를 먼저 쓰고 없으면 문서 날짜로 간다.
      const lineDate = item.shipmentDate || data.shipmentDate || '';
      const monthDay = lineDate.slice(5).replace('-', '/');
      setCell(sheet, `${ITEM_COL.monthDay}${row}`, monthDay);
      setCell(sheet, `${ITEM_COL.name}${row}`, nameWithSpec(item));
      setCell(sheet, `${ITEM_COL.unit}${row}`, item.unit || '');
      setCell(sheet, `${ITEM_COL.qty}${row}`, item.quantity || 0);
      setCell(sheet, `${ITEM_COL.unitPrice}${row}`, num(item.unitPrice));
      setCell(sheet, `${ITEM_COL.supplyAmt}${row}`, num(item.supplyAmt));
      setCell(sheet, `${ITEM_COL.vatAmt}${row}`, num(item.vatAmt));
      setCell(sheet, `${ITEM_COL.remark}${row}`, item.remark || '');
    });

    // 합계는 마지막 장에만 찍는다. 장마다 총액이 찍히면 몇 장짜리인지에 따라
    // 받는 쪽이 금액을 오해한다.
    if (pageIndex === pages.length - 1) {
      setCell(sheet, at(CELL.totalAmt), num(data.totalAmt));
      setCell(sheet, at(CELL.priorBalance), num(data.priorBalance));
      setCell(sheet, at(CELL.totalBalance), num(data.totalBalance));
      setCell(sheet, at(CELL.memo), extra.memo || '');
    } else {
      setCell(sheet, at(CELL.memo), `${pageIndex + 1}쪽 / 총 ${pages.length}쪽 — 합계는 마지막 장에 있습니다`);
    }
  });
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const fileName = `거래명세표_${data.shipmentNumber || ''}.xlsx`;
  saveAs(new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  }), fileName);
};

/** 양식 품목 줄 수. 넘치는 건 화면에서 미리 알려주기 위해 내보낸다. */
export const STATEMENT_ITEM_LIMIT = ITEM_ROW_COUNT;
